import { ArrowRight, Search, Globe, MapPin, Target, Bot, type LucideIcon } from "lucide-react";
import type { CityKey } from "@/lib/citiesData";
import { getCityHubRoute } from "@/lib/cityHubRoutes";

/**
 * "Choose your city service" — surfaced on every city hub and service-area
 * page. Each tile points to the BEST-CONVERTING destination for that
 * city × service combination, in this priority order:
 *
 *   1. City × service spoke route (e.g. /denver-seo, /colorado-springs-ppc)
 *      — most specific, highest local intent match.
 *   2. City digital marketing hub (e.g. /denver-digital-marketing)
 *      — covers all services with city-specific copy.
 *   3. Statewide service hub (e.g. /seo-services, /web-design)
 *      — fallback when no city-specific page exists yet.
 *
 * The map below records which city × service spoke routes actually exist on
 * the site. Add a new entry only when the corresponding spoke page is live —
 * this component is the source of truth for "where do we send users for
 * {city} {service}?" on every city/service-area page.
 */

type ServiceKey = "seo" | "web-design" | "google-maps" | "ppc" | "geo-ai";

type ServiceMeta = {
  key: ServiceKey;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Statewide service hub fallback. */
  hubRoute: string;
};

const SERVICES: ServiceMeta[] = [
  {
    key: "seo",
    label: "SEO",
    description: "Rank for the searches your customers actually use.",
    icon: Search,
    hubRoute: "/colorado-springs-seo",
  },
  {
    key: "web-design",
    label: "Web Design",
    description: "Fast, mobile-first sites built to convert local traffic.",
    icon: Globe,
    hubRoute: "/colorado-springs-web-design",
  },
  {
    key: "google-maps",
    label: "Google Maps",
    description: "Win the Map Pack and dominate Google Business Profile.",
    icon: MapPin,
    hubRoute: "/colorado-springs-google-maps",
  },
  {
    key: "ppc",
    label: "Google Ads / PPC",
    description: "Profitable paid search managed in-house, no markup.",
    icon: Target,
    hubRoute: "/colorado-springs-ppc",
  },
  {
    key: "geo-ai",
    label: "GEO AI",
    description: "Get cited by ChatGPT, Gemini, Perplexity & AI Overviews.",
    icon: Bot,
    hubRoute: "/geo-ai",
  },
];

/**
 * Map of city slug → which service spoke routes exist for that city.
 * Format: `${citySlug}-${routeBase}` matches the actual route file name.
 *
 * Source of truth — keep in sync with src/routes/{city}-{service}.tsx files.
 */
const CITY_SPOKES: Partial<Record<CityKey, Partial<Record<ServiceKey, string>>>> = {
  "colorado-springs": {
    seo: "/colorado-springs-seo",
    "web-design": "/colorado-springs-web-design",
    "google-maps": "/colorado-springs-google-maps",
    ppc: "/colorado-springs-ppc",
    "geo-ai": "/colorado-springs-geo-ai",
  },
  denver: { seo: "/denver-seo", "web-design": "/web-design-denver-colorado" },
  aurora: { seo: "/aurora-seo", "web-design": "/web-design-aurora-colorado" },
  boulder: { seo: "/boulder-seo", "web-design": "/web-design-boulder-colorado" },
  "fort-collins": { seo: "/fort-collins-seo", "web-design": "/web-design-fort-collins-colorado" },
  pueblo: { seo: "/pueblo-seo", "web-design": "/web-design-pueblo-colorado" },
  "castle-rock": { seo: "/castle-rock-seo", "web-design": "/web-design-castle-rock-colorado" },
  parker: { seo: "/parker-seo", "web-design": "/web-design-parker-colorado" },
  monument: { seo: "/monument-seo", "web-design": "/web-design-monument-colorado" },
  arvada: { seo: "/arvada-seo", "web-design": "/web-design-arvada-colorado" },
  lakewood: { seo: "/lakewood-seo", "web-design": "/web-design-lakewood-colorado" },
  thornton: { seo: "/thornton-seo", "web-design": "/web-design-thornton-colorado" },
  westminster: { seo: "/westminster-seo", "web-design": "/web-design-westminster-colorado" },
  centennial: { seo: "/centennial-seo", "web-design": "/web-design-centennial-colorado" },
  longmont: { seo: "/longmont-seo" },
  loveland: { seo: "/loveland-seo" },
  broomfield: { seo: "/broomfield-seo" },
  littleton: { seo: "/littleton-seo" },
  falcon: { seo: "/falcon-seo", "web-design": "/web-design-falcon-colorado" },
  "woodland-park": { seo: "/woodland-park-seo", "web-design": "/web-design-woodland-park-colorado" },
};

/**
 * Returns the best-converting destination for a {city, service} pair.
 *  1) city × service spoke   2) city digital-marketing hub   3) statewide service hub
 */
export function getBestServiceDestination(
  citySlug: CityKey,
  serviceKey: ServiceKey,
  fallbackHub: string,
): { href: string; tier: "spoke" | "city-hub" | "statewide" } {
  const spoke = CITY_SPOKES[citySlug]?.[serviceKey];
  if (spoke) return { href: spoke, tier: "spoke" };
  const cityHub = getCityHubRoute(citySlug);
  if (cityHub) return { href: cityHub, tier: "city-hub" };
  return { href: fallbackHub, tier: "statewide" };
}

const TIER_LABEL: Record<"spoke" | "city-hub" | "statewide", string> = {
  spoke: "Local page",
  "city-hub": "City hub",
  statewide: "Statewide",
};

export function ChooseYourCityService({
  city,
  citySlug,
  heading,
  intro,
}: {
  /** City display name, e.g. "Denver". */
  city: string;
  /** City slug used to resolve spoke/hub routes. */
  citySlug: CityKey;
  heading?: string;
  intro?: string;
}) {
  const headingText = heading ?? `Choose your ${city} service`;
  const introText =
    intro ??
    `Pick the marketing service you need in ${city} — every link goes to the strongest, most specific page we've built for that combination.`;

  return (
    <section
      aria-label={headingText}
      className="border-y border-border bg-background py-16 lg:py-20"
    >
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full border border-brand-gold-deep/30 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold-deep">
            Choose Your Service
          </span>
          <h2 className="mt-5 text-3xl font-extrabold text-foreground sm:text-4xl">
            {headingText}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            {introText}
          </p>
        </div>

        <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => {
            const dest = getBestServiceDestination(citySlug, s.key, s.hubRoute);
            const Icon = s.icon;
            return (
              <li key={s.key}>
                <a
                  href={dest.href}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/60 hover:shadow-card-hover"
                >
                  <div className="flex items-center justify-between">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-secondary text-brand-gold">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="rounded-full bg-brand-gold/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-gold-deep">
                      {TIER_LABEL[dest.tier]}
                    </span>
                  </div>
                  <h3 className="mt-5 text-lg font-extrabold text-foreground group-hover:text-brand-gold-deep">
                    {s.label} in {city}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {s.description}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
                    See {city} {s.label}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
